import React, { useRef, useState } from 'react';
import { Upload, Link2, X, Image as ImageIcon, Camera } from 'lucide-react';
import { api, API, formatApiError } from '@/lib/api';
import { compressImage } from '@/lib/image';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Reusable image picker — paste a URL or upload a file (stored in GridFS).
 * Value is always a string URL (absolute http(s) or relative /api/media/<id>).
 */
export default function ImagePicker({ value, onChange, label, testId, height = 'h-32' }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const fullSrc = (() => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    if (value.startsWith('/api/')) return `${BACKEND_URL}${value}`;
    return value;
  })();

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Dozvoljene su samo slike (jpg, png, webp, gif).');
      return;
    }
    setBusy(true); setError('');
    try {
      // Downscale/compress in the browser first (handles big phone photos & HEIC).
      let toSend = file;
      try {
        toSend = await compressImage(file, { maxDim: 1920, quality: 0.82 });
      } catch (_) {
        toSend = file;
      }
      if (toSend.size > 6 * 1024 * 1024) {
        setError('Slika je prevelika. Pokušajte sa manjom fotografijom.');
        setBusy(false);
        return;
      }
      const form = new FormData();
      form.append('file', toSend);
      const { data } = await api.post('/admin/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="block" data-testid={testId}>
      {label && <span className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</span>}

      <div className={`relative w-full ${height} rounded-2xl border border-dashed border-slate-300 bg-slate-50 overflow-hidden flex items-center justify-center`}>
        {fullSrc ? (
          <img src={fullSrc} alt="" className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.opacity = '0.2'; }} />
        ) : (
          <div className="text-slate-400 text-xs flex flex-col items-center gap-1">
            <ImageIcon className="w-5 h-5" /> Nema slike
          </div>
        )}
        {fullSrc && (
          <button type="button" onClick={() => onChange('')}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 hover:bg-white shadow text-slate-700 flex items-center justify-center"
            aria-label="Ukloni sliku">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-2xl border border-slate-300 focus-within:border-blue-700 bg-white">
          <Link2 className="w-4 h-4 text-slate-500 shrink-0" />
          <input
            type="url"
            placeholder="Nalepi URL slike (Unsplash, Pexels…)"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm min-w-0"
            data-testid={testId ? `${testId}-url` : undefined}
          />
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={testId ? `${testId}-upload` : undefined}
          className="btn-primary px-4 py-2 rounded-2xl font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Upload className="w-4 h-4" /> {busy ? 'Šaljem…' : 'Iz galerije'}
        </button>
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={busy}
          data-testid={testId ? `${testId}-camera` : undefined}
          className="btn-ghost px-4 py-2 rounded-2xl font-semibold text-sm inline-flex sm:hidden items-center justify-center gap-2 disabled:opacity-50"
        >
          <Camera className="w-4 h-4" /> Kamera
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
        />
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">Otpremite fotografiju direktno sa telefona ili računara — automatski se optimizuje.</p>
      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
    </div>
  );
}

export { API };
